import PropTypes from 'prop-types';
import Raven from 'raven-js';
import { Component } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const wasThrownByOriginalChildren = Symbol('wasThrownByOriginalChildren');
/**
 * ErrorBoundary used to catch errors in a non-critical library wrapper component,
 * redirecting errors that originate in the wrapper's children to the consuming app.
 *
 * Usage example:
 *
 *
 *
 * function LibraryComponent({ children }) {
 *  return (
 *    <ChildrenErrorBoundary originalChildren={children}>
 *      <InternalLibraryComponent>
 *        {children}
 *      </InternalLibraryComponent>
 *    </ChildrenErrorBoundary>
 *  )
 * }
 *
 * function ConsumingApp() {
 *  return (
 *    <ErrorBoundary>
 *      <LibraryComponent>
 *        <ConsumingAppChild />
 *      </LibraryComponent>
 *    </ErrorBoundary>
 *  )
 * }

 *
 * - if either InternalLibraryComponent or ConsumingAppChild throw, ChildrenErrorBoundary will catch the error
 *   and fall back to rendering just ConsumingAppChild without the InternalLibraryComponent wrapper
 * - if ConsumingAppChild throws again, the error will bubble up (through any number of nested ChildrenErrorBoundary layers)
 *   to the ErrorBoundary in the ConsumingApp
 */
export default class ChildrenErrorBoundary extends Component {
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  componentDidCatch(error, errorInfo) {
    var _this$props$onError, _this$props;
    const {
      _isOriginalChildren
    } = this.props;
    Raven.captureException(error, {
      extra: {
        errorInfo,
        componentStack: errorInfo.componentStack,
        error
      },
      fingerprint: [this.props.fingerprint],
      tags: {
        lib: 'collaboration-sidebar'
      }
    });
    (_this$props$onError = (_this$props = this.props).onError) === null || _this$props$onError === void 0 || _this$props$onError.call(_this$props, error);
    if (_isOriginalChildren) {
      error[wasThrownByOriginalChildren] = true;
      throw error;
    }
    if (error[wasThrownByOriginalChildren]) {
      // If this error came from `originalChildren`, then it should bubble up
      // for the parent app to handle. Continuing to render `originalChildren`
      // could create an infinite loop.
      throw error;
    }
  }
  render() {
    if (this.state.hasError) {
      return /*#__PURE__*/_jsx(ChildrenErrorBoundary, {
        _isOriginalChildren: true,
        fingerprint: this.props.fingerprint,
        onError: this.props.onError,
        originalChildren: null,
        children: this.props.originalChildren
      });
    }
    return this.props.children;
  }
}
ChildrenErrorBoundary.propTypes = {
  children: PropTypes.node,
  originalChildren: PropTypes.node,
  _isOriginalChildren: PropTypes.bool
};
ChildrenErrorBoundary.defaultProps = {
  children: null,
  originalChildren: null,
  _isOriginalChildren: false
};